// ════════════════════════════════════════════════════════════════════════
//  Klassen und Codes
//
//  Alle Abfragen verlassen sich auf RLS: die Policies filtern auf die eigenen
//  Klassen, deshalb steht hier kein `eq("teacher_id", ...)`. Das doppelt zu
//  filtern würde nur verschleiern, wo die Grenze wirklich gezogen wird.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys, type OttoClient } from "@otto/plattform";

export type SchoolClass = {
  readonly id: string;
  readonly name: string;
  readonly schoolYear: string;
  readonly codeCount: number;
  /** Die Lehrgänge dieser Klasse, in ihrer Reihenfolge. */
  readonly courses: readonly string[];
};

export function useClasses(client: OttoClient, schoolId: string) {
  return useQuery({
    queryKey: queryKeys.classes(schoolId),
    queryFn: async (): Promise<SchoolClass[]> => {
      const { data, error } = await client
        .from("classes")
        // Eine Zeichenkette am Stück: der Client liest den Aufbau aus dem
        // Literal, ein zusammengesetzter Ausdruck ist für ihn nur `string`.
        .select(
          "id, name, school_year, students(count), class_curricula(position, curricula(title))",
        )
        .order("school_year", { ascending: false })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        name: row.name,
        schoolYear: row.school_year,
        // PostgREST liefert die Zählung als Liste mit einem Element.
        codeCount: (row.students as unknown as { count: number }[])[0]?.count ?? 0,
        courses: (
          row.class_curricula as unknown as {
            position: number;
            curricula: { title: string };
          }[]
        )
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((entry) => entry.curricula.title),
      }));
    },
  });
}

/**
 * Klasse anlegen – in zwei Schritten, weil es zwei Zeilen sind.
 *
 * Der Lehrgang steht nicht mehr auf der Klasse, sondern in `class_curricula`.
 * Scheitert der zweite Schritt, bliebe eine Klasse ohne Lehrgang zurück; sie
 * wird deshalb wieder entfernt. Eine Klasse, in der kein Kind üben kann, sieht
 * aus wie eine fertige Klasse - und das ist die unangenehmere Hälfte.
 */
export function useCreateClass(client: OttoClient, schoolId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      schoolYear: string;
      curriculumId: string;
      curriculumVersionId: string;
      teacherId: string;
    }) => {
      const { data, error } = await client
        .from("classes")
        .insert({
          school_id: schoolId,
          teacher_id: input.teacherId,
          name: input.name.trim(),
          school_year: input.schoolYear,
        })
        .select("id")
        .single();
      if (error) throw error;

      const { error: courseError } = await client.from("class_curricula").insert({
        class_id: data.id,
        curriculum_id: input.curriculumId,
        curriculum_version_id: input.curriculumVersionId,
        position: 0,
      });
      if (courseError) {
        await client.from("classes").delete().eq("id", data.id);
        throw courseError;
      }
      return data;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: queryKeys.classes(schoolId) }),
  });
}

/** Kopfdaten einer Klasse – Name und Schuljahr stehen auf den Kärtchen. */
export function useClass(client: OttoClient, classId: string) {
  return useQuery({
    queryKey: queryKeys.schoolClass(classId),
    queryFn: async () => {
      const { data, error } = await client
        .from("classes")
        .select("id, name, school_year")
        .eq("id", classId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: classId !== "",
  });
}
